<template>
  <n-scrollable>
    <n-layout>
      <n-column :span="6" class="title">
        <n-text-model color="accent" size="xl" weight="regular">
          {{ $t('bookRideAuthOTP.title') }}
        </n-text-model>
      </n-column>

      <n-text class="span-6 description" preset="body">
        {{
          $t('bookRideAuthOTP.description', { phoneNumber, phoneCountryCode })
        }}
      </n-text>

      <n-grid :row-gap="5" :bottom-gap="1">
        <n-input
          v-model="otp"
          autocomplete="one-time-code"
          inputmode="numeric"
          pattern="\d{6}"
          focus
          required
          :placeholder="$t('bookRideAuthOTP.codeInputPlaceholder')"
        />

        <n-button :loading="loading" @click="book" block size="lg">
          {{
            $t('bookRideAuthOTP.button', {
              price: $n(actualPrice, 'currency', currency),
            })
          }}
        </n-button>
      </n-grid>

      <recaptcha-button
        ref="recaptcha"
        :loading="isSendingCode"
        @click="resendOTP"
        type="outlined white"
        inverted
        block
      >
        {{ $t('bookRideAuthOTP.resendCode') }}
      </recaptcha-button>

      <recaptcha-terms />
    </n-layout>
  </n-scrollable>
</template>

<script>
import i18n from '@/i18n';
import store from '@/store';
import { mapActions, mapState } from 'vuex';
import PhoneAuth from '@/components/shared/auth/phoneAuth';
import RecaptchaTerms from '@/components/shared/recaptchaTerms';
import RecaptchaButton from '@/components/shared/recaptchaButton';
import { namespacedTypes as userTypes } from '@/store/modules/user-types';
import { initFormbricks } from '@/plugins/Formbricks';

export default {
  name: 'landingBookRideAuthPhone',
  components: {
    PhoneAuth,
    RecaptchaButton,
    RecaptchaTerms,
  },
  props: {
    phoneNumber: {
      type: String,
    },
    phoneCountryCode: {
      type: String,
    },
    exists: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      otp: null,
      loading: false,
      isSendingCode: false,
    };
  },
  computed: {
    ...mapState('trip', ['bookingTrip']),

    actualPrice() {
      return this.bookingTrip.actual_price;
    },
    currency() {
      return this.bookingTrip.price_currency;
    },
    fullPhoneNumber() {
      return `${this.phoneCountryCode}${this.phoneNumber}`;
    },
  },
  methods: {
    ...mapActions('trip', ['bookTrip']),

    async resendOTP(recaptcha) {
      if (this.isSendingCode) {
        return;
      }

      this.isSendingCode = true;

      try {
        await store.dispatch(userTypes.REQUEST_PHONE_AUTH_OTP, {
          phone: this.fullPhoneNumber,
          recaptcha,
        });
      } catch (error) {
        if (error?.response?.status == 429) {
          this.$error({ description: i18n.t('error.auth.otp.throttle_error') });
        } else {
          this.$error();
        }
      } finally {
        this.isSendingCode = false;
        this.otp = '';

        this.$refs?.recaptcha?.reset();
      }
    },

    async book() {
      if (this.loading) {
        return;
      }

      this.loading = true;

      const authenticated = !this.exists
        ? await this.handleRegistration()
        : await this.handleLogin();

      if (!authenticated) {
        this.loading = false;
        return;
      }

      initFormbricks();

      const hasPassengerCost = this.actualPrice > 0;
      if (hasPassengerCost) {
        this.loading = false;
        return this.$router.replace({
          name: 'landing-book-ride.details',
          params: { booked: false, hasPassengerCost: true },
        });
      }

      const booked = await this.handleBooking();

      this.loading = false;

      this.$router.replace({
        name: 'landing-book-ride.details',
        params: { booked },
      });
    },

    async handleRegistration() {
      try {
        const prospectId = this.bookingTrip?.prospect_id ? this.bookingTrip.prospect_id + "" : null;

        await store.dispatch(userTypes.REGISTER_USER, {
          language: store.state.user.profile.language,
          phoneNumber: this.fullPhoneNumber,
          otp: this.otp,
          prospectId,
        });
      } catch (error) {
        this.$error({ description: i18n.t(error?.response?.data?.message) });
        return false;
      }

      return true;
    },

    async handleLogin() {
      try {
        await store.dispatch(userTypes.SIGN_IN_OTP, {
          phone: this.fullPhoneNumber,
          otp: this.otp,
        });
      } catch (e) {
        console.error(e);
        return false;
      }

      return true;
    },

    async handleBooking() {
      try {
        await this.bookTrip();
      } catch (e) {
        const { identifier } = e.response?.data;
        const translationKey = `bookRideDetails.bookingErrors.${identifier}`;
        this.$error(this.$te(translationKey) ? this.$t(translationKey) : null);

        return false;
      } finally {
        this.loading = false;
      }

      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.title {
  margin-top: 0.5rem;
}

.description {
  margin-bottom: 1rem;
}
</style>
